import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Share2, Loader2, IdCard, ArrowRight } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { api, API_URL } from "@/api/client";
import { useI18n } from "@/i18n";
import DigitalCard from "@/components/DigitalCard";
import { Button } from "@/components/ui/button";

export default function Card() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    api("/membership/me")
      .then((m) => setMember(m.member))
      .catch((e) => console.error("Failed to load membership", e))
      .finally(() => setLoading(false));
  }, []);

  const isActive = member?.status === "active";
  const verifyUrl = member?.membership_id ? `${API_URL}/verify/${member.membership_id}?format=html` : "";

  const download = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    const el = cardRef.current;
    const saved = { width: el.style.width, height: el.style.height, maxWidth: el.style.maxWidth };
    try {
      // Always export at a fixed ID-1 card size (1011×638) so the download looks
      // identical & professional on every device with no wrapping/clipping.
      const OUT_W = 1011;
      const OUT_H = 638;
      const BASE_W = OUT_W;
      const BASE_H = OUT_H;
      el.style.width = `${BASE_W}px`;
      el.style.height = `${BASE_H}px`;
      el.style.maxWidth = "none";
      el.classList.add("capturing");
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
        logging: false,
        width: BASE_W,
        height: BASE_H,
        windowWidth: 1200,
        windowHeight: BASE_H,
      });
      // Normalise to EXACT 1011×638
      const out = document.createElement("canvas");
      out.width = OUT_W;
      out.height = OUT_H;
      out.getContext("2d").drawImage(canvas, 0, 0, OUT_W, OUT_H);
      const link = document.createElement("a");
      link.download = `VHRP-${member.membership_id}.png`;
      link.href = out.toDataURL("image/png");
      link.click();
      toast.success(t("cardSaved"));
    } catch {
      toast.error(t("cardSaveFailed"));
    } finally {
      el.classList.remove("capturing");
      el.style.width = saved.width;
      el.style.height = saved.height;
      el.style.maxWidth = saved.maxWidth;
      setDownloading(false);
    }
  };

  const share = async () => {
    const text = `${t("cardShareMsg")} ${verifyUrl}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: t("cardHeadTitle"), text });
        return;
      } catch (e) { console.debug("Native share dismissed", e); }
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("copied"));
    } catch {
      toast.error(t("cardSaveFailed"));
    }
  };

  if (loading) {
    return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  }

  if (!isActive) {
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-tint text-brand"><IdCard size={30} /></div>
        <h1 className="mt-5 text-2xl font-black text-ink">{t("cardEmptyTitle")}</h1>
        <p className="mt-2 text-ink-muted">{t("cardEmptySub")}</p>
        <Button data-testid="card-get-membership" onClick={() => navigate("/membership")} className="mt-6 h-12 rounded-full bg-brand px-8 font-bold hover:bg-brand-dark">
          {t("cardGet")} <ArrowRight size={16} className="ml-1" />
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <h1 className="text-2xl font-black text-ink">{t("cardHeadTitle")}</h1>

      <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_320px]">
        <div>
          <div className="mx-auto max-w-lg">
            <DigitalCard ref={cardRef} member={member} showQr />
          </div>
          <div className="mt-5 flex gap-3">
            <Button data-testid="card-download" onClick={download} disabled={downloading} className="h-11 flex-1 rounded-full bg-brand font-bold hover:bg-brand-dark">
              {downloading ? <Loader2 className="animate-spin" /> : <><Download size={17} className="mr-2" /> {t("cardSave")}</>}
            </Button>
            <Button data-testid="card-share" onClick={share} variant="outline" className="h-11 flex-1 rounded-full font-bold">
              <Share2 size={17} className="mr-2" /> {t("cardShare")}
            </Button>
          </div>
        </div>

        <div className="rounded-3xl border border-line bg-white p-6 text-center">
          <h2 className="text-sm font-bold uppercase tracking-wide text-brand">{t("cardScan")}</h2>
          <div className="mt-4 inline-block rounded-2xl border border-line p-4">
            {verifyUrl && <QRCodeCanvas value={verifyUrl} size={168} includeMargin />}
          </div>
          <p className="mt-3 text-xs text-ink-muted">{t("cardScanHint")}</p>
          <div className="mt-5 space-y-2 text-left text-sm">
            <Row label={t("dFullName")} value={member.full_name} />
            <Row label={t("dFather")} value={member.father_name} />
            <Row label={t("dDob")} value={member.dob} />
            <Row label={t("dPhone")} value={member.phone} />
            <Row label={t("cardCity")} value={member.city} />
            <Row label={t("cardState")} value={member.state} />
            <Row label={t("dTier")} value={`₹${member.tier}`} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 border-b border-line pb-1.5">
      <span className="text-ink-muted">{label}</span>
      <span className="font-semibold text-ink">{value || "—"}</span>
    </div>
  );
}
